import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixIamManagementSignonV1SignOnPolicyServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/sign-on-policy-service',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/sign-on-policy-service',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/iam/signon/v1/sign-on-policies',
        destPath: '/v1/sign-on-policies',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/iam/signon/v1/sign-on-policies',
        destPath: '/v1/sign-on-policies',
      },
    ],
    _: [
      {
        srcPath: '/iam/signon/v1/sign-on-policies',
        destPath: '/v1/sign-on-policies',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_identity_sign-on-policy';

/**
 * Retrieves the calling site's sign-on policy.
 *
 * The request takes no parameters. The site is resolved from the request context.
 *
 * A site that has never updated its policy still gets a successful response,
 * with each requirement set to its default of `false`.
 *
 * The response currently contains `requireOwnerApproval` and `requireEmailVerification`.
 *
 * This method needs a minimum amount of time left on the request deadline,
 * 200 ms by default. A call that arrives with less time remaining fails with
 * `INSUFFICIENT_DEADLINE_BUDGET`.
 */
export function getSignOnPolicy(payload: object): RequestOptionsFactory<any> {
  function __getSignOnPolicy({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.iam.signon.v1.sign_on_policy',
      method: 'GET' as any,
      methodFqn:
        'wix.iam.management.signon.v1.SignOnPolicyService.GetSignOnPolicy',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamManagementSignonV1SignOnPolicyServiceUrl({
        protoPath: '/v1/sign-on-policies',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'signOnPolicy.createdDate' },
              { path: 'signOnPolicy.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getSignOnPolicy;
}

/**
 * Updates the calling site's sign-on policy.
 *
 * Partial updates are supported. Specify only the requirements you want to change,
 * and the rest keep their current values.
 *
 * Changing the policy affects members who sign up from that point on.
 * Existing members aren't re-evaluated, so turning on `requireOwnerApproval`
 * doesn't move already-active members back to pending.
 *
 * Fails with `NOT_FOUND` when the site doesn't have a sign-on policy yet,
 * and with `INVALID_ARGUMENT` when the request contains an unsupported value.
 */
export function updateSignOnPolicy(
  payload: object
): RequestOptionsFactory<any> {
  function __updateSignOnPolicy({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'signOnPolicy.createdDate' },
          { path: 'signOnPolicy.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.iam.signon.v1.sign_on_policy',
      method: 'PATCH' as any,
      methodFqn:
        'wix.iam.management.signon.v1.SignOnPolicyService.UpdateSignOnPolicy',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixIamManagementSignonV1SignOnPolicyServiceUrl({
        protoPath: '/v1/sign-on-policies',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'signOnPolicy.createdDate' },
              { path: 'signOnPolicy.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateSignOnPolicy;
}
